import { useEffect, useState } from "react";
import type { TopicDetailResponse } from "../api/contract.gen.ts";
import { useServices } from "../app/services.ts";
import { duration, label } from "../data/display.ts";
import { mediaUrl } from "../data/media.ts";
import { Icon } from "../ui/Icon.tsx";
import styles from "./TopicPanel.module.css";

/** A topic next to its chat (docs/web-designs screen 4): summary, sections, play one or all. */
export function TopicPanel({ topicId, onClose, onOpenMessage }: { topicId: string; onClose: () => void; onOpenMessage: (conversationId: string, messageId: string) => void }) {
  const services = useServices();
  const [detail, setDetail] = useState<TopicDetailResponse>();
  const [error, setError] = useState(false);

  useEffect(() => {
    setDetail(undefined);
    services.session.call<TopicDetailResponse>(`/v1/topics/${topicId}`).then(setDetail, () => setError(true));
  }, [topicId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function play(messageId: string, conversationId: string, fromMs: number) {
    if (!services.store.message(conversationId, messageId)) await services.sync.loadMessages(conversationId);
    const message = services.store.message(conversationId, messageId);
    if (!message || message.response.kind !== "voice") return onOpenMessage(conversationId, messageId);
    const conversation = services.store.conversations.get(conversationId);
    const sender = conversation?.members.find((m) => m.user.id === message.response.sender_id)?.user;
    const url = await mediaUrl(message, "audio", () => services.refresh(message));
    await services.player.play(
      {
        messageId,
        conversationId,
        senderName: message.response.sender_id === services.store.me ? "Du" : label(sender),
        durationMs: message.response.duration_ms,
        sections: (message.response.segments ?? []).map((s) => ({ startMs: s.start_ms, title: s.title })),
        reportHeard: message.response.sender_id !== services.store.me,
      },
      url,
      fromMs,
    );
  }

  const topic = detail?.topic;
  const conversation = topic ? services.store.conversations.get(topic.conversation_id) : undefined;
  return (
    <aside className={styles.panel} aria-label="Thema">
      <header className={styles.head}>
        <Icon name="topic" />
        <b>{topic?.title ?? "Thema"}</b>
        <button onClick={onClose} aria-label="Schließen">
          <Icon name="close" />
        </button>
      </header>
      <div className={styles.body}>
        {error && <p className={styles.note}>Das Thema ließ sich nicht laden.</p>}
        {!detail && !error && <p className={styles.note}>Wird geladen …</p>}
        {topic && detail && (
          <>
            <div className={styles.pills}>
              <span className={topic.resolved ? styles.done : styles.open}>{topic.resolved ? "erledigt" : "offen"}</span>
              <span className={styles.grey}>
                {topic.segment_count} Abschnitte · {topic.participant_count} Personen
              </span>
            </div>
            {topic.summary && <p className={styles.summary}>{topic.summary}</p>}
            {detail.segments.length > 0 && (
              <button className={styles.playAll} onClick={() => void play(detail.segments[0]!.message_id, topic.conversation_id, detail.segments[0]!.segment.start_ms)}>
                <Icon name="play" size={18} /> Ab dem ersten Abschnitt abspielen
              </button>
            )}
            <div className={styles.label}>ABSCHNITTE</div>
            {detail.segments.map((s) => {
              const sender = conversation?.members.find((m) => m.user.id === s.sender_id)?.user;
              return (
                <div key={`${s.message_id}-${s.segment.idx}`} className={styles.segment}>
                  <button className={styles.text} onClick={() => onOpenMessage(topic.conversation_id, s.message_id)}>
                    <b>{s.segment.title}</b>
                    <span>
                      {s.sender_id === services.store.me ? "Du" : label(sender)} · {new Date(s.message_created_at).toLocaleDateString("de-AT", { day: "numeric", month: "short" })} ·{" "}
                      {duration(s.segment.end_ms - s.segment.start_ms)}
                    </span>
                  </button>
                  <button className={styles.play} onClick={() => void play(s.message_id, topic.conversation_id, s.segment.start_ms)} aria-label="Abschnitt abspielen">
                    <Icon name="play" size={18} />
                  </button>
                </div>
              );
            })}
          </>
        )}
      </div>
    </aside>
  );
}
